import { useCallback, useEffect, useRef, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useServices } from '@/context/ServiceContext'
import { useAuth } from '@/context/AuthContext'
import { LoadingSpinner } from '@/components/LoadingSpinner'
import { domainEvents } from '@/services/events/domainEvents'
import type { Episode } from '@/domain/episode'
import type { EpisodeProgress } from '@/domain/progress'

// Per Document 2 Module 2 acceptance criteria: an episode counts as
// "complete" once >= 90% of duration has been watched.
const COMPLETE_THRESHOLD = 0.9
const SAVE_INTERVAL_MS = 5000

export function EpisodePlayerPage() {
  const { id } = useParams<{ id: string }>()
  const { episodes, progress, analytics } = useServices()
  const { currentUser } = useAuth()
  const navigate = useNavigate()

  const videoRef = useRef<HTMLVideoElement>(null)
  const startedRef = useRef(false)
  const completedRef = useRef(false)
  const lastSaveRef = useRef(0)

  const [episode, setEpisode] = useState<Episode | null>(null)
  const [loading, setLoading] = useState(true)
  const [justCompleted, setJustCompleted] = useState(false)

  useEffect(() => {
    if (!id) return
    episodes.getById(id).then((ep) => {
      setEpisode(ep)
      setLoading(false)
    })
  }, [id, episodes])

  const persistProgress = useCallback(
    (positionSeconds: number, completed: boolean) => {
      if (!currentUser || !episode) return
      const record: EpisodeProgress = {
        episodeId: episode.id,
        userId: currentUser.id,
        orgId: currentUser.orgId,
        positionSeconds,
        completed,
        startedAt: new Date().toISOString(),
        completedAt: completed ? new Date().toISOString() : null,
      }
      progress.upsert(record)
    },
    [currentUser, episode, progress]
  )

  const handlePlay = () => {
    if (startedRef.current || !episode || !currentUser) return
    startedRef.current = true
    domainEvents.emit('episode.started', { episodeId: episode.id, userId: currentUser.id })
    analytics.track('episode_started', { episodeId: episode.id })
    persistProgress(0, false)
  }

  const handleTimeUpdate = () => {
    if (!videoRef.current || !episode) return
    const { currentTime, duration } = videoRef.current
    const now = Date.now()
    if (now - lastSaveRef.current > SAVE_INTERVAL_MS) {
      lastSaveRef.current = now
      persistProgress(currentTime, completedRef.current)
    }
    if (!completedRef.current && duration && currentTime / duration >= COMPLETE_THRESHOLD) {
      markComplete(currentTime)
    }
  }

  const markComplete = (positionSeconds: number) => {
    if (completedRef.current || !episode || !currentUser) return
    completedRef.current = true
    setJustCompleted(true)
    persistProgress(positionSeconds, true)
    domainEvents.emit('episode.completed', { episodeId: episode.id, userId: currentUser.id })
    analytics.track('episode_completed', { episodeId: episode.id })
  }

  const handleEnded = () => {
    if (videoRef.current) markComplete(videoRef.current.duration || 0)
  }

  useEffect(() => {
    return () => {
      if (videoRef.current && startedRef.current) {
        persistProgress(videoRef.current.currentTime, completedRef.current)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [episode])

  if (loading) return <LoadingSpinner label="Loading player…" />
  if (!episode) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-20 text-center">
        <h1 className="font-display text-2xl font-bold text-white">Episode not found</h1>
        <Link to="/episodes" className="cyber-btn-secondary mt-6 inline-flex">
          ← Back to Library
        </Link>
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-4xl px-4 py-10 sm:px-6 lg:px-8">
      <Link to={`/episodes/${episode.id}`} className="text-sm text-white/50 hover:text-white">
        ← {episode.title}
      </Link>

      <div className="mt-4 overflow-hidden rounded-2xl bg-black shadow-2xl shadow-teal-500/10">
        <video
          ref={videoRef}
          key={episode.id}
          src={episode.videoUrl}
          controls
          className="aspect-video w-full"
          onPlay={handlePlay}
          onTimeUpdate={handleTimeUpdate}
          onEnded={handleEnded}
        />
      </div>

      <p className="mt-3 text-xs text-white/40">
        Placeholder sample media — real CyberTales animation drops in without changing this player
        (Document 11 §6).
      </p>

      {justCompleted && (
        <div className="mt-6 cyber-panel flex flex-col items-start gap-3 border-teal-500/30 p-5 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <span className="text-2xl">🎉</span>
            <div>
              <p className="font-display font-semibold text-white">Episode complete!</p>
              <p className="text-sm text-white/60">Test what you learned and earn Cyber Points.</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <button onClick={() => navigate(`/episodes/${episode.id}/quiz`)} className="cyber-btn-primary whitespace-nowrap">
              Take the Quiz
            </button>
            <button onClick={() => navigate('/episodes')} className="cyber-btn-secondary whitespace-nowrap">
              Explore more episodes
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
